import type { FileModel, GalleryModel } from '@/models/gallery.model'
import { getLogger } from '@/logger'

/** Reason a metadata refresh was triggered; used only for logging context. */
export type MetadataRefreshReason = 'preview' | 'download'

/** Snapshot of selection/active-file identity captured before a refresh. */
interface RefreshContext {
  selectedKeys: Set<string>
  activeKey: string | null
}

/** Gallery state re-derived after a metadata refresh, ready to apply to the UI. */
export interface RefreshedGalleryState {
  gallery: GalleryModel
  selectedIds: Set<string>
  activeIndex: number | null
}

/** Parameters accepted by {@link MetadataRefreshService.refreshOnce}. */
export interface RefreshOnceParams {
  gallery: GalleryModel
  selectedIds: Set<string>
  activeIndex: number | null
  /** Fetches the latest gallery metadata (e.g. re-signed URLs). */
  fetchLatest: () => Promise<GalleryModel>
  reason: MetadataRefreshReason
}

export interface MetadataRefreshService {
  /**
   * Refreshes gallery metadata at most once per {@link reset} call.
   *
   * Re-fetches the gallery and re-maps the caller's selection and active
   * index onto the refreshed file list by file identity (name + size +
   * content type), since signed URLs change but file identity does not.
   *
   * @returns The refreshed state, or `null` if a refresh was already
   * attempted or the fetch failed (in which case the failure is logged).
   */
  refreshOnce(params: RefreshOnceParams): Promise<RefreshedGalleryState | null>
  /** Clears the one-shot guard so a new refresh can be attempted. */
  reset(): void
}

function fileIdentityKey(file: FileModel): string {
  return `${file.name}::${file.size}::${file.contentType}`
}

function captureRefreshContext(
  gallery: GalleryModel,
  selectedIds: Set<string>,
  activeIndex: number | null,
): RefreshContext {
  const selectedKeys = new Set(
    gallery.files.filter(file => selectedIds.has(file.id)).map(file => fileIdentityKey(file)),
  )

  const activeKey =
    activeIndex !== null && gallery.files[activeIndex] ? fileIdentityKey(gallery.files[activeIndex]) : null

  return { selectedKeys, activeKey }
}

function applyRefreshedGallery(
  gallery: GalleryModel,
  context: RefreshContext,
  hadActiveIndex: boolean,
): RefreshedGalleryState {
  const refreshedSelectedIds = gallery.files
    .filter(file => context.selectedKeys.has(fileIdentityKey(file)))
    .map(file => file.id)
  const selectedIds = new Set(refreshedSelectedIds)

  if (!hadActiveIndex) {
    return { gallery, selectedIds, activeIndex: null }
  }

  const nextActiveIndex =
    context.activeKey !== null ? gallery.files.findIndex(file => fileIdentityKey(file) === context.activeKey) : -1
  const activeIndex = nextActiveIndex >= 0 ? nextActiveIndex : gallery.files.length > 0 ? 0 : null

  return { gallery, selectedIds, activeIndex }
}

/**
 * Creates a one-shot gallery metadata refresh service.
 *
 * Used to recover from expired signed URLs (e.g. a 403 on download, or a
 * preview image failing to load) by re-fetching the gallery once and
 * re-mapping the current selection/active file onto the refreshed data.
 */
export function createMetadataRefreshService(): MetadataRefreshService {
  let attempted = false

  const reset = (): void => {
    attempted = false
  }

  const refreshOnce = async ({
    gallery,
    selectedIds,
    activeIndex,
    fetchLatest,
    reason,
  }: RefreshOnceParams): Promise<RefreshedGalleryState | null> => {
    if (attempted) {
      return null
    }
    attempted = true

    const context = captureRefreshContext(gallery, selectedIds, activeIndex)

    try {
      const refreshed = await fetchLatest()
      const result = applyRefreshedGallery(refreshed, context, activeIndex !== null)
      getLogger().info('[goto-gallery] Share metadata refreshed after signed URL failure', { reason })
      return result
    } catch (refreshErr) {
      getLogger().error('[goto-gallery] Failed to refresh share metadata', { reason, refreshErr })
      return null
    }
  }

  return { refreshOnce, reset }
}
